export default {
  hero: {
    title: "Vendez votre surplus d'énergie solaire aux consommateurs locaux",
    description:
      "Gagnez plus que les tarifs de rachat en vendant directement à vos voisins au sein de votre communauté. Nous gérons la complexité, vous recevez les paiements.",
    cta: "Commencez à vendre",
    "bullet-1": "Revenus supérieurs aux tarifs de rachat",
    "bullet-2": "Pas d'administration complexe",
    "bullet-3": "Paiements mensuels réguliers",
  },
  "why-sell": {
    title: "Pourquoi vendre via le solaire communautaire ?",
    subtitle:
      "Il s'agit d'une décision commerciale, pas d'une question d'idéologie. Découvrez pourquoi les producteurs choisissent le commerce local d'énergie.",
    cards: {
      "higher-revenue": {
        title: "Revenus plus élevés",
        description:
          "Vendez aux prix du marché local au lieu des tarifs de rachat réglementés. Gagnez jusqu'à 30 % de plus par kWh.",
      },
      "predictable-income": {
        title: "Revenu prévisible",
        description:
          "Gérez les autorisations efficacement pour fluidifier les workflows d'équipe et renforcer la collaboration.",
      },
      "local-value": {
        title: "Valeur locale",
        description:
          "Simplifiez les processus et améliorez l'efficacité avec des systèmes intelligents.",
      },
    },
  },
  "how-it-works": {
    title: "Comment ça marche ?",
    subtitle: "De la production au paiement, un parcours fluide géré pour vous.",
    steps: {
      "step-1": {
        title: "Votre installation produit",
        description:
          "Vos panneaux solaires produisent de l'électricité comme d'habitude. L'énergie excédentaire que vous ne consommez pas peut être partagée.",
      },
      "step-2": {
        title: "Énergie allouée localement",
        description:
          "Votre surplus d'énergie est alloué aux consommateurs à proximité, dans un rayon de 2 à 20 km, grâce au cadre réglementé d'Enedis.",
      },
      "step-3": {
        title: "Nous gérons tout",
        description:
          "ProjetSolaire gère la mise en place du PMO, la gestion des entités juridiques, les clés de répartition, la coordination de l'API Enedis, les contrats et la conformité.",
      },
      "step-4": {
        title: "Vous recevez les paiements",
        description:
          "Recevez des paiements mensuels basés sur les kWh réellement livrés.",
      },
    },
  },
  "need-consumers": {
    "title-before": "Vous avez besoin de",
    "title-highlight": " consommateurs ",
    "title-after": "? Nous vous aidons à les trouver.",
    description:
      "Ne vous inquiétez pas pour trouver des acheteurs locaux pour votre surplus d'énergie. Nous vous aidons à recruter votre installateur qui active la prospection locale pour construire votre communauté.",
    benefits: {
      "local-targeting": "Ciblage local dans votre zone éligible",
      "qualified-leads": "Prospects qualifiés issus de ménages intéressés",
      "community-support": "Soutien et matériel pour le développement communautaire",
    },
    cta: "Commencez à créer votre communauté",
    stats: {
      "consumers-in-network": "Consommateurs en réseau",
      "successful-matches": "Correspondances réussies",
      "avg-time-to-full": "Temps moyen à la charge",
      "partner-installers": "Installateurs partenaires",
    },
  },
  eligibility: {
    "title-before": "Êtes-vous",
    "title-highlight": "admissible",
    "title-after": " ?",
    subtitle:
      "Vérifiez rapidement si votre installation est éligible au commerce communautaire d'énergie solaire.",
    "most-qualify": "La plupart des installations solaires sont admissibles",
    paragraph:
      "Si vous disposez d'une installation solaire en état de marche équipée d'un compteur Linky et que vous avez des consommateurs potentiels à proximité, vous pouvez probablement commencer à gagner davantage grâce à votre surplus d'énergie.",
    cta: "Vérifier mon éligibilité",
    criteria: {
      technical: {
        title: "Technique",
        items: {
          enedis: "Connexion Enedis active (MES approuvé)",
          linky: "Compteur Linky installé",
          installation: "Installation solaire en exploitation",
        },
      },
      geographic: {
        title: "Géographique",
        items: {
          urban: "Consommateurs dans un rayon de 2 km (zones urbaines)",
          rural: "Jusqu'à 20 km pour les installations rurales",
          zone: "Même zone du réseau de distribution",
        },
      },
      contractual: {
        title: "Contractuel",
        items: {
          injection: "Injection partielle ou totale du surplus",
          contracts: "Pas de contrats exclusifs à long terme",
          willingness: "Volonté de participer à la communauté",
        },
      },
    },
  },
  monitor: {
    title: "Surveillez votre communauté",
    subtitle:
      "Gardez le contrôle grâce à des informations en temps réel sur les performances de votre communauté énergétique.",
    "image-alt":
      "Tableau de bord : indicateurs de la communauté, graphiques et liste des consommateurs",
  },
  "calculate-earnings": {
    title: "Calculez vos revenus potentiels",
    subtitle:
      "Découvrez combien vous pourriez gagner en plus en vendant à votre communauté plutôt qu'au tarif de rachat.",
    "installation-size": "Taille de l'installation",
    "surplus-energy": "Énergie excédentaire",
    "feed-in-rate": "Tarif de rachat actuel",
    "additional-revenue": "Revenus annuels supplémentaires",
    "more-than-feed-in": "30 % de plus que les tarifs de rachat",
    "feed-in-tariff": "Tarif de rachat",
    "community-solar": "Solaire communautaire",
    "surplus-kwh": "Énergie excédentaire",
    "info-note":
      "Projections indicatives selon vos données et la demande locale de consommation.",
    cta: "Obtenir une offre personnalisée",
    "slider-min": "min",
    "slider-max": "max",
  },
  pricing: {
    title: "Tarification transparente, alignée sur la performance",
    subtitle:
      "Une tarification simple, basée sur un pourcentage, adaptée à l'ampleur de votre projet.",
    "plan-title": "Plan du producteur",
    "plan-subtitle": "Tout ce dont vous avez besoin pour vendre localement",
    "what-you-get": "Ce que vous obtenez",
    "pricing-rows": {
      "setup-fee": { label: "Frais d'installation", subtitle: "Unique, avant impôts" },
      "monthly-management": {
        label: "Gestion mensuelle",
        subtitle: "Facturé mensuellement",
      },
      "transaction-fee": {
        label: "Frais de transaction",
        subtitle: "Du prix du kWh",
      },
    },
    features: {
      pmo: "Gestion complète du PMO",
      "consumer-acquisition": "Acquisition de consommateurs",
      "enedis-api": "Coordination API Enedis",
      billing: "Facturation et paiement",
    },
    comparison: {
      "buying-column": "Achat de solaire",
      "leasing-column": "Location de panneaux",
      "upfront-cost": {
        criteria: "Coût initial",
        buying: "Élevé",
        leasing: "Aucun",
      },
      "monthly-cost": {
        criteria: "Coût mensuel",
        buying: "Aucun",
        leasing: "Forfait mensuel fixe",
      },
      maintenance: {
        criteria: "Maintenance",
        buying: "À votre charge",
        leasing: "Incluse",
      },
      warranty: {
        criteria: "Garantie",
        buying: "Limitée",
        leasing: "Couverture complète",
      },
      flexibility: {
        criteria: "Flexibilité",
        buying: "Faible",
        leasing: "Rachat après 10 ans",
      },
    },
  },
  "we-handle-complexity": {
    title: "Nous gérons la complexité",
    subtitle:
      "Tout ce qui serait pénible sans nous est géré de manière transparente.",
    cards: {
      "ongoing-support": {
        title: "Assistance continue",
        description:
          "Une équipe d'assistance dédiée pour vous aider, vous et vos clients, à répondre à toutes vos questions ou résoudre tous vos problèmes.",
      },
      "legal-pmo": {
        title: "Entité juridique et gestion du PMO",
        description:
          "Nous nous occupons de la création et de la gestion de la structure juridique de votre communauté énergétique.",
      },
      "enedis-coordination": {
        title: "Coordination Enedis",
        description:
          "Toutes les communications avec les opérateurs de réseau, les clés de répartition et les exigences techniques sont gérées pour vous.",
      },
      regulatory: {
        title: "Conformité réglementaire",
        description:
          "Restez automatiquement en conformité avec toutes les réglementations énergétiques et les exigences en matière de reporting.",
      },
      billing: {
        title: "Facturation et paiement",
        description:
          "Cycles de facturation automatisés, recouvrement des paiements et répartition transparente des revenus.",
      },
      "consumer-onboarding": {
        title: "Intégration des consommateurs",
        description:
          "Nous recrutons, intégrons et gérons le consentement des consommateurs locaux dans votre communauté.",
      },
    },
  },
  cta: {
    title: "Votre énergie solaire vaut plus qu'un tarif réglementé",
    subtitle:
      "Rejoignez des centaines de producteurs qui gagnent jusqu'à trois fois plus en vendant directement aux consommateurs locaux.",
    "no-credit-check": "Sans vérification de crédit",
    "quick-process": "Processus rapide",
    "no-obligation": "Sans engagement",
    button: "Trouver une communauté près de chez moi",
  },
}
